import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase/config';
import { logError } from '../utils/errorHandler';
import { products as localProducts, categories as localCategories, brands as localBrands, banners as localBanners } from '../data/products';

const CatalogContext = createContext();

const slugify = (value) => String(value || '')
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9&]+/g, '-')
  .replace(/^-+|-+$/g, '');

const normalizeProduct = (data, docId) => {
  const raw = data || {};
  const id = raw.id ?? docId;
  const price = Number.isFinite(Number(raw.price)) ? Number(raw.price) : 0;
  const originalPrice = raw.originalPrice != null && Number.isFinite(Number(raw.originalPrice))
    ? Number(raw.originalPrice)
    : null;
  const image = raw.image || (Array.isArray(raw.images) && raw.images[0]) || '';
  const images = Array.isArray(raw.images) && raw.images.length > 0
    ? raw.images
    : image
      ? [image]
      : [];

  return {
    id: String(id),
    name: raw.name || 'Untitled Product',
    category: raw.category || 'uncategorized',
    subcategory: raw.subcategory || '',
    brand: raw.brand || 'Generic',
    price,
    originalPrice,
    discount: raw.discount ?? null,
    currency: raw.currency || '₦',
    image,
    images,
    inStock: raw.inStock !== false,
    isActive: raw.isActive !== false,
    description: raw.description || '',
    url: raw.url || '',
    specifications: raw.specifications || null,
    tags: Array.isArray(raw.tags) ? raw.tags : [],
    featured: raw.featured === true,
    updatedAt: raw.updatedAt || null,
    createdAt: raw.createdAt || null
  };
};

const normalizeCategory = (data, docId) => {
  const raw = data || {};
  const id = raw.id || raw.name || docId;
  const name = raw.name || raw.id || 'Uncategorized';
  return {
    id: String(id),
    name,
    slug: raw.slug || slugify(name),
    icon: raw.icon || '🛍️',
    image: raw.image || '',
    description: raw.description || '',
    order: typeof raw.order === 'number' ? raw.order : 0
  };
};

const normalizeBrand = (data, docId) => {
  const raw = data || {};
  const name = raw.name || raw.id || docId || 'Brand';
  return {
    id: String(raw.id || raw.slug || slugify(name)),
    name,
    slug: raw.slug || slugify(name),
    logo: raw.logo || '🏷️',
    country: raw.country || ''
  };
};

const normalizeBanner = (data, docId) => {
  const raw = data || {};
  const id = raw.id ?? docId;
  return {
    id: String(id),
    title: raw.title || 'Featured Collection',
    subtitle: raw.subtitle || '',
    image: raw.image || '',
    bgColor: raw.bgColor || '#111111',
    link: raw.link || '/shop',
    buttonText: raw.buttonText || 'Shop Now',
    order: typeof raw.order === 'number' ? raw.order : 0,
    isActive: raw.isActive !== false
  };
};

const sortByName = (a, b) => (a?.name || '').localeCompare(b?.name || '');
const sortByOrder = (a, b) => (a?.order ?? 0) - (b?.order ?? 0);

export const CatalogProvider = ({ children }) => {
  const [rawProducts, setRawProducts] = useState(localProducts.map((item) => normalizeProduct(item, String(item.id))));
  const [categories, setCategories] = useState(localCategories.map((item) => normalizeCategory(item, String(item.id || item.slug || item.name))));
  const [brands, setBrands] = useState(localBrands.map((item) => normalizeBrand(item, String(item.id || item.slug || item.name))));
  const [banners, setBanners] = useState(localBanners.map((item) => normalizeBanner(item, String(item.id))));
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState({ products: 'local', categories: 'local', brands: 'local', banners: 'local' });

  useEffect(() => {
    let isMounted = true;
    let pending = 4;
    const listeners = [];

    const markLoaded = () => {
      pending -= 1;
      if (pending <= 0 && isMounted) {
        setLoading(false);
      }
    };

    const registerListener = (collectionName, setState, fallback, normalize, sorter) => {
      const baseRef = collection(db, collectionName);
      const ref = sorter?.orderBy
        ? query(baseRef, orderBy(sorter.orderBy, sorter.direction || 'asc'))
        : baseRef;

      const unsubscribe = onSnapshot(ref, (snapshot) => {
        if (!isMounted) return;
        if (snapshot.empty) {
          setState(fallback);
          setSources((prev) => ({ ...prev, [collectionName]: 'local' }));
          markLoaded();
          return;
        }

        const items = snapshot.docs.map((docSnap) => normalize(docSnap.data(), docSnap.id));
        const sorted = sorter?.fn ? [...items].sort(sorter.fn) : items;
        setState(sorted);
        setSources((prev) => ({ ...prev, [collectionName]: 'firestore' }));
        markLoaded();
      }, (error) => {
        logError(error, { action: 'catalogSnapshot', collection: collectionName });
        if (!isMounted) return;
        setState(fallback);
        setSources((prev) => ({ ...prev, [collectionName]: 'local' }));
        markLoaded();
      });

      listeners.push(unsubscribe);
    };

    registerListener(
      'products',
      setRawProducts,
      localProducts.map((item) => normalizeProduct(item, String(item.id))),
      normalizeProduct
    );
    registerListener(
      'categories',
      setCategories,
      localCategories.map((item) => normalizeCategory(item, String(item.id || item.slug || item.name))),
      normalizeCategory,
      { fn: sortByName }
    );
    registerListener(
      'brands',
      setBrands,
      localBrands.map((item) => normalizeBrand(item, String(item.id || item.slug || item.name))),
      normalizeBrand,
      { fn: sortByName }
    );
    registerListener(
      'banners',
      setBanners,
      localBanners.map((item) => normalizeBanner(item, String(item.id))),
      normalizeBanner,
      { fn: sortByOrder, orderBy: 'order' }
    );

    return () => {
      isMounted = false;
      listeners.forEach((unsubscribe) => unsubscribe());
    };
  }, []);

  const categoryIndex = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      if (category?.id) {
        map.set(String(category.id).toLowerCase(), category);
      }
      if (category?.slug) {
        map.set(String(category.slug).toLowerCase(), category);
      }
    });
    return map;
  }, [categories]);

  const allProducts = useMemo(() => rawProducts.map((product) => {
    const key = String(product.category || '').toLowerCase();
    const match = categoryIndex.get(key);
    return {
      ...product,
      categorySlug: match?.slug || slugify(product.category),
      categoryName: match?.name || product.category
    };
  }), [rawProducts, categoryIndex]);

  const products = useMemo(() => allProducts.filter((product) => product.isActive !== false), [allProducts]);
  const activeBanners = useMemo(() => banners.filter((banner) => banner.isActive !== false), [banners]);

  const value = useMemo(() => ({
    products,
    allProducts,
    categories,
    brands,
    banners: activeBanners,
    rawBanners: banners,
    loading,
    sources
  }), [products, allProducts, categories, brands, activeBanners, banners, loading, sources]);

  return (
    <CatalogContext.Provider value={value}>
      {children}
    </CatalogContext.Provider>
  );
};

export const useCatalog = () => {
  const context = useContext(CatalogContext);
  if (!context) {
    throw new Error('useCatalog must be used within CatalogProvider');
  }
  return context;
};
